import { useCallback, useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';

export function useUnsavedChangesWarning(isDirty, alwaysWarnOnUnload = false) {
  const [pendingClose, setPendingClose] = useState(null);

  useEffect(() => {
    if (!isDirty && !alwaysWarnOnUnload) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, alwaysWarnOnUnload]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  const guardClose = useCallback(
    (actualClose) => {
      if (!isDirty) {
        actualClose();
        return;
      }
      setPendingClose(() => actualClose);
    },
    [isDirty],
  );

  const onConfirm = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
    if (pendingClose) {
      pendingClose();
      setPendingClose(null);
    }
  }, [blocker, pendingClose]);

  const onCancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    setPendingClose(null);
  }, [blocker]);

  const open = blocker.state === 'blocked' || !!pendingClose;

  return { guardClose, confirmProps: { open, onConfirm, onCancel } };
}
